import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Send, Play, Calendar, Plus, Filter,
  CheckCircle2, Clock, AlertTriangle, Crown, Sparkles, Info,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import NativeSelect from "@/components/forms/NativeSelect";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import api from "@/lib/api";

/**
 * AdminSubscriptions — full subscription management console.
 *
 * Sections:
 *   1. Maintenance banner — the big "Run Maintenance" trigger + last-run stats.
 *      (Because scheduling is manual per business decision, this is the primary
 *      operational surface admins visit periodically.)
 *   2. Filter bar — plan (featured/premium/any) × status (any/active/soon/expired).
 *   3. Table — one row per paid spa with all key data + inline actions.
 *   4. Per-row action dialogs — Extend, Set Expiry, Renew, Send Reminder.
 *
 * All destructive/state-changing calls are optimistic on the row (immediate
 * badge/date update) with a server refetch on completion to guarantee the
 * displayed data matches DB truth.
 */

const STATUS_META = {
  active:        { label: "Active",         icon: CheckCircle2,   chip: "bg-[#DCFCE7] text-[#065F46] border-[#86EFAC]" },
  expiring_soon: { label: "Expiring Soon",  icon: Clock,          chip: "bg-[#FEF3C7] text-[#92400E] border-[#FCD34D]" },
  expired:       { label: "Expired",        icon: AlertTriangle,  chip: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]" },
};

const PLAN_META = {
  featured: { label: "Featured", icon: Sparkles, chip: "bg-[#EEF2FF] text-[#1E3A8A] border-[#93C5FD]" },
  premium:  { label: "Premium",  icon: Crown,    chip: "bg-[#F5EFE6] text-[#7A5A22] border-[#C9A86A]" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch { return "—"; }
}

export default function AdminSubscriptions() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [lastRun, setLastRun] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState("");   // "" | "active" | ...
  const [planFilter, setPlanFilter] = useState("");       // "" | "featured" | "premium"

  // Modal state — one shared dialog switches shape based on `action`
  const [dialog, setDialog] = useState(null);   // { action, row, days?, expiryDate? }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status_filter = statusFilter;
      if (planFilter) params.plan = planFilter;
      const { data } = await api.get("/admin/subscriptions", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load subscriptions");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, planFilter]);

  useEffect(() => { load(); }, [load]);

  // Quick summary for the top banner (aggregates over the CURRENT filtered set)
  const summary = useMemo(() => {
    const s = { total: items.length, active: 0, expiring_soon: 0, expired: 0 };
    for (const r of items) s[r.status] = (s[r.status] || 0) + 1;
    return s;
  }, [items]);

  const runMaintenance = async () => {
    if (!window.confirm(
      "Run maintenance sweep now?\n\n" +
      "This will:\n" +
      "  • Send any pending renewal reminders (7d / 3d / 1d / expiry / final).\n" +
      "  • Auto-downgrade any expired Featured/Premium plans.\n\n" +
      "The action is idempotent — safe to run multiple times."
    )) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/subscriptions/run-maintenance");
      setLastRun({ ...data.stats, ran_at: data.ran_at });
      const s = data.stats || {};
      const emailsSent = (s.reminders_7d || 0) + (s.reminders_3d || 0) + (s.reminders_1d || 0) + (s.expiry_emails || 0) + (s.final_reminders || 0);
      toast.success(
        `Maintenance complete — ${emailsSent} email${emailsSent === 1 ? "" : "s"} sent, ` +
        `${s.downgraded || 0} downgraded, ${s.notifications_created || 0} in-app alert${s.notifications_created === 1 ? "" : "s"}.`
      );
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Maintenance run failed");
    } finally {
      setRunning(false);
    }
  };

  const runBackfill = async () => {
    if (!window.confirm(
      "Backfill missing subscription dates?\n\n" +
      "Any Featured/Premium spa without a subscription window will be initialised " +
      "with a fresh 30-day cycle starting today. Existing dates are preserved."
    )) return;
    try {
      const { data } = await api.post("/admin/subscriptions/backfill");
      toast.success(`Backfilled ${data.updated_count} subscription${data.updated_count === 1 ? "" : "s"}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backfill failed");
    }
  };

  const openDialog = (action, row) => {
    setDialog({
      action,
      row,
      days: 30,
      expiryDate: row.expiry_date ? row.expiry_date.slice(0, 10) : "",
    });
  };

  const submitDialog = async () => {
    if (!dialog) return;
    const { action, row } = dialog;
    try {
      if (action === "extend") {
        await api.post(`/admin/subscriptions/${row.spa_id}/extend`, { days: parseInt(dialog.days, 10) || 0 });
        toast.success(`Extended ${row.spa_name} by ${dialog.days} days.`);
      } else if (action === "set-expiry") {
        await api.post(`/admin/subscriptions/${row.spa_id}/set-expiry`, { expiry_date: dialog.expiryDate });
        toast.success(`Expiry updated for ${row.spa_name}.`);
      } else if (action === "renew") {
        await api.post(`/admin/subscriptions/${row.spa_id}/renew`);
        toast.success(`${row.spa_name} renewed for another 30 days.`);
      } else if (action === "send-reminder") {
        await api.post(`/admin/subscriptions/${row.spa_id}/send-reminder`);
        toast.success(`Reminder sent to ${row.owner?.email || "owner"}.`);
      }
      setDialog(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  return (
    <div data-testid="admin-subscriptions-page" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#7A5A22]">Billing</div>
          <h1 className="font-heading text-2xl sm:text-3xl text-[#111827] mt-1">Subscription Management</h1>
          <p className="text-sm text-[#4B5563] mt-1 max-w-2xl">
            Monitor every Featured &amp; Premium subscription. Trigger reminder emails, extend
            expiries, and downgrade lapsed plans via the maintenance sweep.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            variant="outline"
            className="gap-2"
            onClick={runBackfill}
            data-testid="admin-subscriptions-backfill-button"
          >
            <Plus size={14} /> Backfill
          </Button>
          <Button
            className="gap-2 bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white"
            onClick={runMaintenance}
            disabled={running}
            data-testid="admin-subscriptions-run-maintenance-button"
          >
            <Play size={14} /> {running ? "Running…" : "Run Maintenance"}
          </Button>
        </div>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryTile label="Total" value={summary.total} tone="neutral" />
        <SummaryTile label="Active" value={summary.active || 0} tone="green" icon={CheckCircle2} />
        <SummaryTile label="Expiring Soon" value={summary.expiring_soon || 0} tone="amber" icon={Clock} />
        <SummaryTile label="Expired" value={summary.expired || 0} tone="red" icon={AlertTriangle} />
      </div>

      {/* Last-run stats */}
      {lastRun && (
        <div className="rounded-xl border border-[#E7DDCF] bg-[#FBF7F1] px-4 py-3 flex items-start gap-2 text-sm" data-testid="last-run-summary">
          <Info size={16} className="text-[#7A5A22] mt-0.5 shrink-0" />
          <div className="text-[#4B5563] leading-relaxed">
            <div className="font-medium text-[#111827]">
              Last maintenance run: {new Date(lastRun.ran_at).toLocaleString()}
            </div>
            Reminders sent → 7d: {lastRun.reminders_7d} · 3d: {lastRun.reminders_3d} · 1d: {lastRun.reminders_1d} ·
            Expiry: {lastRun.expiry_emails} · Final: {lastRun.final_reminders}.
            Downgraded: {lastRun.downgraded}. In-app alerts: {lastRun.notifications_created}.
            {lastRun.email_failures > 0 && <span className="text-[#B42318]"> Failures: {lastRun.email_failures}.</span>}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] p-4 flex flex-wrap items-end gap-3">
        <div className="min-w-[160px]">
          <Label className="text-xs flex items-center gap-1"><Filter size={12} /> Status</Label>
          <NativeSelect
            value={statusFilter}
            onChange={(v) => setStatusFilter(v)}
            data-testid="admin-subscriptions-filter-status"
            className="mt-1"
          >
            <option value="">All Statuses</option>
            <option value="active">Active</option>
            <option value="expiring_soon">Expiring Soon (≤ 7 days)</option>
            <option value="expired">Expired</option>
          </NativeSelect>
        </div>
        <div className="min-w-[160px]">
          <Label className="text-xs flex items-center gap-1"><Filter size={12} /> Plan</Label>
          <NativeSelect
            value={planFilter}
            onChange={(v) => setPlanFilter(v)}
            data-testid="admin-subscriptions-filter-plan"
            className="mt-1"
          >
            <option value="">All Plans</option>
            <option value="featured">Featured only</option>
            <option value="premium">Premium only</option>
          </NativeSelect>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={load} className="gap-2" data-testid="admin-subscriptions-refresh">
            <RefreshCw size={13} /> Refresh
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-2xl bg-white border border-[#E7DDCF] overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-[#4B5563]">Loading subscriptions…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-sm text-[#4B5563]">
            No subscriptions match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-widest text-[#7A5A22] bg-[#FBF7F1] border-b border-[#E7DDCF]">
                  <th className="px-4 py-3">Spa</th>
                  <th className="px-4 py-3">Owner</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Start</th>
                  <th className="px-4 py-3">Expiry</th>
                  <th className="px-4 py-3">Days Left</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const sm = STATUS_META[row.status] || STATUS_META.active;
                  const pm = PLAN_META[row.plan] || PLAN_META.featured;
                  const SIcon = sm.icon;
                  const PIcon = pm.icon;
                  const daysLeft = row.days_remaining;
                  const daysDisplay = daysLeft == null ? "—" : (daysLeft < 0 ? `${Math.abs(daysLeft)} ago` : `${daysLeft}`);
                  const daysColor = daysLeft == null ? "text-[#4B5563]" : (daysLeft < 0 ? "text-[#B42318]" : (daysLeft <= 7 ? "text-[#92400E]" : "text-[#111827]"));
                  return (
                    <tr key={row.spa_id} className="border-b border-[#F5EFE6] hover:bg-[#FBF7F0] transition-colors" data-testid={`admin-subscription-row-${row.spa_id}`}>
                      <td className="px-4 py-3">
                        <div className="font-medium text-[#111827] truncate max-w-[220px]" title={row.spa_name}>{row.spa_name}</div>
                        <div className="text-xs text-[#7A5A22]">/{row.slug}</div>
                      </td>
                      <td className="px-4 py-3">
                        {row.owner ? (
                          <div>
                            <div className="text-[#111827] truncate max-w-[180px]" title={row.owner.name}>{row.owner.name || "—"}</div>
                            <div className="text-xs text-[#4B5563] truncate max-w-[180px]" title={row.owner.email}>{row.owner.email}</div>
                          </div>
                        ) : <span className="text-xs text-[#4B5563]">No owner</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${pm.chip}`}>
                          <PIcon size={11} /> {pm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sm.chip}`} data-testid={`row-status-${row.spa_id}`}>
                          <SIcon size={11} /> {sm.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[#111827]">{fmtDate(row.start_date)}</td>
                      <td className="px-4 py-3 text-[#111827]">{fmtDate(row.expiry_date)}</td>
                      <td className={`px-4 py-3 font-medium ${daysColor}`}>{daysDisplay}</td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <ActionBtn onClick={() => openDialog("send-reminder", row)} title="Send reminder email" testId={`row-remind-${row.spa_id}`}>
                            <Send size={12} />
                          </ActionBtn>
                          <ActionBtn onClick={() => openDialog("extend", row)} title="Extend subscription" testId={`row-extend-${row.spa_id}`}>
                            <Plus size={12} />
                          </ActionBtn>
                          <ActionBtn onClick={() => openDialog("set-expiry", row)} title="Set expiry date" testId={`row-set-expiry-${row.spa_id}`}>
                            <Calendar size={12} />
                          </ActionBtn>
                          <Button
                            size="sm"
                            onClick={() => openDialog("renew", row)}
                            className="h-7 px-2 text-xs bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white gap-1"
                            data-testid={`row-renew-${row.spa_id}`}
                          >
                            <RefreshCw size={11} /> Renew
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Action dialog */}
      <Dialog open={!!dialog} onOpenChange={(v) => !v && setDialog(null)}>
        <DialogContent className="bg-white" data-testid="admin-subscriptions-action-dialog">
          {dialog && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {dialog.action === "extend" && "Extend Subscription"}
                  {dialog.action === "set-expiry" && "Set Expiry Date"}
                  {dialog.action === "renew" && "Renew Subscription"}
                  {dialog.action === "send-reminder" && "Send Renewal Reminder"}
                </DialogTitle>
                <DialogDescription>
                  {dialog.row.spa_name} · {dialog.row.owner?.email || "no owner email"}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {dialog.action === "extend" && (
                  <div>
                    <Label className="text-xs">Days to add (relative to current expiry)</Label>
                    <Input
                      type="number" min={1} max={3650}
                      value={dialog.days}
                      onChange={(e) => setDialog({ ...dialog, days: e.target.value })}
                      data-testid="dialog-extend-days-input"
                      className="mt-1"
                    />
                    <div className="text-xs text-[#4B5563] mt-1">
                      Current expiry: <strong>{fmtDate(dialog.row.expiry_date)}</strong>
                    </div>
                  </div>
                )}

                {dialog.action === "set-expiry" && (
                  <div>
                    <Label className="text-xs">New expiry date</Label>
                    <Input
                      type="date"
                      value={dialog.expiryDate}
                      onChange={(e) => setDialog({ ...dialog, expiryDate: e.target.value })}
                      data-testid="dialog-set-expiry-date-input"
                      className="mt-1"
                    />
                  </div>
                )}

                {dialog.action === "renew" && (
                  <div className="text-sm text-[#4B5563]">
                    This will reset the subscription cycle to a fresh 30 days from today
                    and restore Featured/Premium benefits if they had been swept off.
                    A renewal confirmation email + in-app alert are sent to the owner.
                  </div>
                )}

                {dialog.action === "send-reminder" && (
                  <div className="text-sm text-[#4B5563]">
                    Immediately send a renewal reminder email — bypasses the normal
                    schedule (7d/3d/1d) and uses the tone appropriate for the spa&apos;s
                    current position in the cycle.
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setDialog(null)} data-testid="dialog-cancel">Cancel</Button>
                <Button
                  onClick={submitDialog}
                  className="bg-[#1F4E3D] hover:bg-[#2E5D4E] text-white"
                  data-testid="dialog-confirm"
                >
                  Confirm
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SummaryTile({ label, value, tone = "neutral", icon: Icon }) {
  const tones = {
    neutral: "bg-white border-[#E7DDCF] text-[#111827]",
    green:   "bg-[#F0FDF4] border-[#86EFAC] text-[#065F46]",
    amber:   "bg-[#FFFBEB] border-[#FCD34D] text-[#92400E]",
    red:     "bg-[#FEF2F2] border-[#FCA5A5] text-[#991B1B]",
  };
  return (
    <div className={`rounded-2xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest opacity-70">
        {Icon && <Icon size={12} />} {label}
      </div>
      <div className="font-heading text-3xl mt-1">{value}</div>
    </div>
  );
}

function ActionBtn({ children, onClick, title, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      data-testid={testId}
      className="inline-flex items-center justify-center h-7 w-7 rounded-lg text-[#4B5563] hover:bg-[#F5EFE6] hover:text-[#111827] transition-colors"
    >
      {children}
    </button>
  );
}
